import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

export const TOKEN_KEY = "st_token";
const NOTICE_KEY = "gz_auth_notice";
// Pages that must work without a session — a 401 there is an ordinary result, not an expiry.
const PUBLIC_PATHS = ["/login", "/account/setup"];

// Human messages for the backend's stable 401 codes (see backend auth_service.current_user).
const SESSION_NOTICES = {
  SESSION_EXPIRED: "Your session has expired. Please sign in again.",
  SESSION_REVOKED: "You were signed out. Please sign in again.",
  ACCOUNT_DISABLED: "This account has been disabled. Contact an administrator.",
  SESSION_INVALID: "Please sign in again.",
  INVALID_TOKEN: "Please sign in again.",
};

export function takeAuthNotice() {
  try {
    const n = sessionStorage.getItem(NOTICE_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
    return n;
  } catch { return null; }
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const onPublicPage = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
    const hadToken = !!localStorage.getItem(TOKEN_KEY);
    if (err.response?.status === 401 && !onPublicPage && hadToken) {
      const notice = SESSION_NOTICES[err.response?.data?.code];
      try { if (notice) sessionStorage.setItem(NOTICE_KEY, notice); } catch { /* storage unavailable */ }
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  // Structured refusals ({code, message, ...}) carry their own human message.
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  return e?.message || "Something went wrong";
}

/** The server's refusal as text: a string detail as-is, a structured {code, message} detail by
 * its message, else `fallback`. Never an object (React cannot render one in a toast). */
export function detailText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return fallback;
}

/** A refusal's message PLUS the exact failing checks the server names (`detail.reasons`), e.g.
 *  OPPORTUNITY_STALE -> NOT_TRADABLE_STALE. The message alone hid which check failed. */
export function refusalText(e, fallback) {
  const base = detailText(e, fallback);
  const reasons = e?.response?.data?.detail?.reasons;
  return Array.isArray(reasons) && reasons.length ? `${base} (${reasons.join(", ")})` : base;
}

export function apiErrorCode(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.code) return d.code;
  return e?.response?.data?.code || null;
}

export default api;
